import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DeploymentOptions } from '../components/DeploymentOptions';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Documentation sections
const documentationSections = {
  gettingStarted: {
    title: 'Getting Started',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    items: [
      {
        id: 'introduction',
        title: 'Introduction',
        content: `
Our suite of AI-powered applications is designed to streamline your workflow and enhance productivity. Whether you're a developer, content creator, or business professional, our tools are built to help you work smarter.

### Key Features

- **AI Integration**: Powered by state-of-the-art language models
- **Enterprise Ready**: Built with security and scalability in mind
- **Modern UI/UX**: Intuitive interfaces for seamless user experience
- **Extensible**: Easy integration with existing workflows
        `
      },
      {
        id: 'quick-start',
        title: 'Quick Start Guide',
        content: `
### 1. Account Setup
- Sign up for an account
- Configure your API keys
- Set up your organization preferences

### 2. First Steps
- Browse available applications
- Try the AI Toybox to explore capabilities
- Set up your first integration

### 3. Basic Usage
- Navigate the dashboard
- Launch applications
- Save and export your work
        `
      }
    ]
  },
  appLifecycle: {
    title: 'App Lifecycle',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    items: [
      {
        id: 'selecting-app',
        title: 'Selecting Your App',
        content: `
### How to Choose the Right App

1. **Identify Your Needs**
- Determine your specific use case
- Consider your target audience
- Review required features

2. **Compare Available Apps**
- Browse our app categories
- Read feature descriptions
- Check system requirements

3. **Try Before You Deploy**
- Use the AI Toybox for testing
- Experiment with sample data
- Evaluate performance

### App Categories

- **Content Creation**: StoryWeaver, PowerPoint Generator
- **Business Tools**: Grant Writer, Grant Finder
- **Data & Analytics**: SQL Writer, PySpark Writer
- **Marketing**: Social Media Manager, Branding Agent
        `
      },
      {
        id: 'deployment-guide',
        title: 'Deployment Guide',
        content: `
### Deployment Process

1. **Preparation**
- Review system requirements
- Prepare your data
- Set up authentication

2. **Configuration**
- Choose deployment environment
- Set environment variables
- Configure API endpoints

3. **Testing**
- Run integration tests
- Verify connections
- Monitor performance

4. **Going Live**
- Deploy to production
- Set up monitoring
- Enable logging

### Best Practices
- Start with a staging environment
- Use version control
- Implement CI/CD pipelines
- Monitor resource usage
        `
      },
      {
        id: 'monitoring-insights',
        title: 'Monitoring & Insights',
        content: `
### Understanding Your Analytics

1. **Key Metrics**
- Usage statistics
- Performance indicators
- Error rates
- User engagement

2. **Optimization**
- Resource utilization
- Response times
- Cost analysis
- Scaling decisions

3. **User Feedback**
- Satisfaction scores
- Feature requests
- Bug reports
- Usage patterns

### Available Tools
- Real-time monitoring
- Custom dashboards
- Automated alerts
- Performance reports
        `
      }
    ]
  },
  pricing: {
    title: 'Pricing & Subscription',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    items: [
      {
        id: 'subscription-plans',
        title: 'Subscription Plans',
        content: `
### AURABLOX Basic Plan

Our Basic subscription plan provides access to our suite of AI-powered applications and features:

- $9.99 USD per month
- 14-day free trial
- No credit card required to start trial
- Cancel anytime

### What's Included

- Access to all AI applications
- Regular updates and new features
- Standard support
- API access (with usage limits)

### Free Trial Details

- 14-day full access trial
- No credit card required to start
- Automatic reminder before trial ends
- Option to cancel anytime

### Payment Methods

- Credit/Debit Cards
- Apple Pay
- Additional payment methods may be available in your region

### Subscription Management

- Easy subscription management through customer portal
- Flexible billing options
- Transparent usage tracking
- Email notifications for billing events

### Enterprise Options

For enterprise customers requiring custom solutions, volume licensing, or additional features, please contact our sales team.
        `
      },
      {
        id: 'billing-faq',
        title: 'Billing FAQ',
        content: `
### Common Questions

1. How do I start my free trial?
- Sign up for an account
- No credit card required
- Immediate access to all features
- Automatic reminder before trial ends

2. What happens after the trial?
- You'll receive a reminder before trial ends
- Option to enter payment details
- Subscription starts only after trial
- No charges if you decide not to continue

3. Can I cancel my subscription?
- Yes, cancel anytime
- No cancellation fees
- Access continues until billing period ends
- Option to reactivate later

4. How is billing handled?
- Monthly billing cycle
- Secure payment processing via Stripe
- Detailed invoices provided
- Automatic renewal unless cancelled

### Need Help?

For any billing-related questions or support:
- Contact our billing support team
- Check our billing documentation
- Visit the customer portal
- Email: support@aurablox.com
        `
      }
    ]
  },
  applications: {
    title: 'Applications',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
    items: [
      {
        id: 'ai-toybox',
        title: 'AI Toybox',
        content: `
### Overview
AI Toybox is your playground for exploring AI capabilities. Test and experiment with various AI features in a safe environment.

### Features
- Text generation and completion
- Image generation with DALL-E
- Document analysis
- Social media content creation

### Usage
1. Select your desired AI feature
2. Input your requirements
3. Generate and refine results
4. Export or save your output
        `
      },
      {
        id: 'grant-writer',
        title: 'Grant Writer',
        content: `
### Overview
Grant Writer helps you create compelling grant proposals using AI assistance, making the grant writing process more efficient and effective.

### Features
- Proposal template generation
- Budget section assistance
- Project narrative creation
- Goals and objectives formulation

### Best Practices
- Provide detailed project information
- Review and customize generated content
- Include specific funding requirements
- Follow grantor guidelines closely
        `
      },
      {
        id: 'grant-finder',
        title: 'Grant Finder',
        content: `
### Overview
Grant Finder helps organizations discover relevant grant opportunities using AI-powered matching algorithms.

### Features
- Advanced grant search
- Eligibility matching
- Deadline tracking
- Grant requirement analysis

### Usage
1. Enter organization details
2. Specify funding requirements
3. Review matched opportunities
4. Track application status
        `
      },
      {
        id: 'social-media-manager',
        title: 'Social Media Manager',
        content: `
### Overview
AI-powered tool for creating and managing social media content across multiple platforms.

### Features
- Content generation for multiple platforms
- Post scheduling
- Engagement analytics
- Hashtag optimization
- Content calendar management

### Best Practices
- Define your brand voice
- Set content themes
- Schedule posts strategically
- Monitor engagement metrics
        `
      },
      {
        id: 'powerpoint-generator',
        title: 'PowerPoint Generator',
        content: `
### Overview
Create professional presentations quickly with AI assistance and beautiful templates.

### Features
- Slide content generation
- Template selection
- Design customization
- Image suggestions
- Export options

### Usage
1. Choose presentation type
2. Input key points
3. Select design theme
4. Customize and export
        `
      },
      {
        id: 'lesson-plan-creator',
        title: 'Lesson Plan Creator',
        content: `
### Overview
Create comprehensive lesson plans aligned with educational standards and learning objectives.

### Features
- Curriculum alignment
- Activity suggestions
- Assessment creation
- Resource recommendations
- Learning objective generation

### Best Practices
- Define clear learning objectives
- Include diverse activities
- Plan for different learning styles
- Add assessment methods
        `
      },
      {
        id: 'course-outline-creator',
        title: 'Course Outline Creator',
        content: `
### Overview
Design structured course outlines with learning objectives, modules, and assessments.

### Features
- Module planning
- Learning outcome definition
- Resource integration
- Timeline planning
- Assessment strategy

### Usage
1. Define course objectives
2. Create module structure
3. Add learning activities
4. Plan assessments
        `
      },
      {
        id: 'sql-writer',
        title: 'SQL Writer',
        content: `
### Overview
Generate optimized SQL queries from natural language descriptions.

### Features
- Natural language to SQL conversion
- Query optimization
- Schema analysis
- Query validation
- Performance suggestions

### Best Practices
- Be specific in requirements
- Review generated queries
- Test with sample data
- Consider indexing suggestions
        `
      },
      {
        id: 'pyspark-writer',
        title: 'PySpark Writer',
        content: `
### Overview
Create efficient PySpark transformations and analytics code using AI assistance.

### Features
- Code generation for data transformations
- Performance optimization
- Best practice suggestions
- Error handling
- Documentation generation

### Usage
1. Describe desired transformation
2. Review generated code
3. Customize as needed
4. Test and deploy
        `
      },
      {
        id: 'data-dictionary-generator',
        title: 'Data Dictionary Generator',
        content: `
### Overview
Create comprehensive data dictionaries and documentation for databases and datasets.

### Features
- Automated metadata extraction
- Schema documentation
- Relationship mapping
- Data quality rules
- Export capabilities

### Best Practices
- Include all relevant fields
- Document relationships
- Define data types clearly
- Add business context
        `
      },
      {
        id: 'training-manual-creator',
        title: 'Training Manual Creator',
        content: `
### Overview
Generate detailed training manuals and documentation for processes and systems.

### Features
- Content structure generation
- Step-by-step guides
- Visual aid suggestions
- Interactive elements
- PDF export

### Usage
1. Define training objectives
2. Outline key processes
3. Add examples and exercises
4. Review and customize
        `
      },
      {
        id: 'research-assistant',
        title: 'Research Assistant',
        content: `
### Overview
AI-powered tool for conducting research, analyzing sources, and generating summaries.

### Features
- Literature review
- Source analysis
- Citation management
- Summary generation
- Research organization

### Best Practices
- Define research scope
- Verify sources
- Use multiple databases
- Document methodology
        `
      },
      {
        id: 'play-formatter',
        title: 'Play Formatter',
        content: `
### Overview
Format and structure play scripts and screenplays according to industry standards.

### Features
- Script formatting
- Character dialogue formatting
- Scene breakdown
- Stage direction formatting
- Export options

### Usage
1. Input raw script
2. Select format style
3. Review formatting
4. Export final version
        `
      },
      {
        id: 'life-coach',
        title: 'Life Coach',
        content: `
### Overview
AI-powered personal development assistant providing guidance and goal-setting support.

### Features
- Goal setting assistance
- Progress tracking
- Action plan creation
- Motivation techniques
- Habit formation

### Best Practices
- Set SMART goals
- Track progress regularly
- Review and adjust plans
- Celebrate milestones
        `
      },
      {
        id: 'auto-rag',
        title: 'AutoRag',
        content: `
### Overview
Automated Retrieval Augmented Generation (RAG) system for processing and analyzing documents.

### Features
- Document processing
- Content extraction
- Knowledge base creation
- Query answering
- Source attribution

### Usage
1. Upload documents
2. Process content
3. Query information
4. Review results
        `
      },
      {
        id: 'branding-agent',
        title: 'Branding Agent',
        content: `
### Overview
AI-powered branding assistant for creating consistent and engaging brand content.

### Features
- Brand voice development
- Content guidelines
- Visual identity suggestions
- Marketing material generation
- Brand consistency checking

### Best Practices
- Define brand values
- Maintain consistency
- Track brand metrics
- Regular brand audits
        `
      }
    ]
  },
  deployment: {
    title: 'Deployment',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    ),
    items: [
      {
        id: 'self-hosted',
        title: 'Self-Hosted Deployment',
        content: `
### Requirements
- Docker and Docker Compose
- Node.js 16+
- PostgreSQL 13+
- Redis (optional)

### Installation Steps
1. Clone the repository
2. Configure environment variables
3. Build Docker images
4. Run database migrations
5. Start the application

### Configuration
- API keys setup
- Database configuration
- Cache settings
- Security options
        `
      },
      {
        id: 'cloud-deployment',
        title: 'Cloud Deployment',
        content: `
### Supported Platforms
- AWS
- Google Cloud
- Azure
- Digital Ocean

### Deployment Process
1. Choose your cloud provider
2. Configure infrastructure
3. Set up CI/CD pipeline
4. Deploy application
5. Monitor and scale

### Best Practices
- Use infrastructure as code
- Implement proper monitoring
- Set up automated backups
- Configure auto-scaling
        `
      }
    ]
  },
  security: {
    title: 'Security',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    items: [
      {
        id: 'security-overview',
        title: 'Security Overview',
        content: `
### Security Features
- End-to-end encryption
- Role-based access control
- API key management
- Audit logging

### Best Practices
- Regular security audits
- Password policies
- Data backup strategies
- Incident response plan
        `
      },
      {
        id: 'compliance',
        title: 'Compliance',
        content: `
### Standards
- GDPR compliance
- HIPAA readiness
- SOC 2 certification
- ISO 27001 alignment

### Data Protection
- Data encryption at rest
- Secure data transmission
- Regular backups
- Data retention policies
        `
      }
    ]
  },
  apiIntegration: {
    title: 'API Integration',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    items: [
      {
        id: 'api-overview',
        title: 'API Overview',
        content: `
### REST API Documentation

Our REST API enables seamless integration with your existing systems and workflows.

### Authentication

- **API Keys**: Secure your requests with API keys
- **OAuth 2.0**: Enterprise-grade authentication
- **Rate Limiting**: Fair usage policies and quotas

### Common Endpoints

\`\`\`
GET /api/v1/apps
POST /api/v1/deploy
GET /api/v1/metrics
\`\`\`

### Response Formats

- JSON-formatted responses
- Detailed error messages
- Pagination support
        `
      },
      {
        id: 'webhooks',
        title: 'Webhooks',
        content: `
### Webhook Integration

Configure webhooks to receive real-time updates about your applications.

### Available Events

- Deployment status changes
- Performance alerts
- Usage thresholds
- Error notifications

### Best Practices

- Implement retry logic
- Verify webhook signatures
- Handle events asynchronously
- Monitor webhook health
        `
      },
      {
        id: 'sdks-libraries',
        title: 'SDKs & Libraries',
        content: `
### Official SDKs

- **Python SDK**: Full-featured Python client
- **Node.js SDK**: JavaScript/TypeScript support
- **Java SDK**: Enterprise Java integration
- **Go SDK**: High-performance Go client

### Community Libraries

- Ruby gems
- PHP packages
- .NET libraries
- Mobile SDKs
        `
      }
    ]
  },
  bestPractices: {
    title: 'Best Practices',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    items: [
      {
        id: 'architecture',
        title: 'Architecture Patterns',
        content: `
### Recommended Architectures

1. **Microservices**
- Service isolation
- Independent scaling
- Fault tolerance

2. **Event-Driven**
- Real-time processing
- Loose coupling
- Scalable pipelines

3. **Hybrid Cloud**
- Multi-cloud deployment
- Geographic distribution
- Disaster recovery
        `
      },
      {
        id: 'security',
        title: 'Security Guidelines',
        content: `
### Security Best Practices

1. **Data Protection**
- Encryption at rest
- TLS in transit
- Key rotation

2. **Access Control**
- Role-based access
- Least privilege
- Regular audits

3. **Compliance**
- GDPR compliance
- SOC 2 certification
- HIPAA readiness
        `
      }
    ]
  },
  troubleshooting: {
    title: 'Troubleshooting',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
    items: [
      {
        id: 'common-issues',
        title: 'Common Issues',
        content: `
### Frequently Encountered Issues

1. **Connection Problems**
- Network connectivity
- Authentication failures
- API timeouts

2. **Performance Issues**
- Resource constraints
- Bottlenecks
- Memory leaks

3. **Integration Challenges**
- Version mismatches
- Configuration errors
- Data format issues

### Resolution Steps
- Diagnostic procedures
- Log analysis
- Performance profiling
        `
      },
      {
        id: 'debugging',
        title: 'Debugging Guide',
        content: `
### Debugging Tools

1. **Log Analysis**
- Centralized logging
- Log correlation
- Error tracking

2. **Monitoring**
- Resource metrics
- Performance traces
- Health checks

3. **Troubleshooting Steps**
- Issue identification
- Root cause analysis
- Resolution verification
        `
      }
    ]
  }
};

export default function Documents() {
  const { currentUser } = useAuth();
  const [selectedSection, setSelectedSection] = useState('gettingStarted');
  const [selectedItem, setSelectedItem] = useState('introduction');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const allItems = Object.values(documentationSections).flatMap(section => 
      section.items.map(item => ({
        ...item,
        section: section.title,
        sectionId: Object.keys(documentationSections).find(key => documentationSections[key] === section)
      }))
    );
    
    if (searchQuery) {
      setFilteredItems(allItems.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.content.toLowerCase().includes(searchQuery.toLowerCase())
      ));
    } else {
      setFilteredItems([]);
    }
  }, [searchQuery]);

  const renderMarkdown = (content) => {
    return content.split('\n').map((line, index) => {
      if (line.startsWith('### ')) {
        return (
          <h3 key={index} className="text-xl font-semibold mt-8 mb-4 text-gray-900 flex items-center group">
            {line.replace('### ', '')}
            <button 
              onClick={() => navigator.clipboard.writeText(`#${line.replace('### ', '').toLowerCase().replace(/\s+/g, '-')}`)}
              className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Copy link"
            >
              <svg className="w-5 h-5 text-gray-400 hover:text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </button>
          </h3>
        );
      } else if (line.startsWith('- ')) {
        return (
          <li key={index} className="ml-4 mb-2 text-gray-700 flex items-start">
            <span className="mr-2 mt-1.5">•</span>
            <span>{line.replace('- ', '')}</span>
          </li>
        );
      } else if (line.match(/^\d+\./)) {
        return (
          <div key={index} className="ml-4 mb-2 text-gray-700 flex items-start">
            <span className="mr-2 font-medium text-primary-500">{line.match(/^\d+/)[0]}.</span>
            <span>{line.replace(/^\d+\.\s*/, '')}</span>
          </div>
        );
      } else if (line.trim() === '') {
        return <div key={index} className="h-4"></div>;
      } else {
        return <p key={index} className="mb-4 text-gray-700 leading-relaxed">{line}</p>;
      }
    });
  };

  const getCurrentContent = () => {
    const section = documentationSections[selectedSection];
    if (!section) return null;
    
    const item = section.items.find(item => item.id === selectedItem);
    return item ? renderMarkdown(item.content) : null;
  };

  const handleActionButton = (action) => {
    switch (action) {
      case 'browse-apps':
        navigate('/dashboard');
        break;
      case 'deploy':
        navigate('/deployment-manager');
        break;
      case 'insights':
        navigate('/insights');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Documentation & Guides"
          subtitle="Comprehensive guides and documentation for all AURABLOX features"
          actions={
            <div className="relative">
              <input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <svg
                className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          }
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-3">
              <nav className="space-y-8 sticky top-8">
                {Object.entries(documentationSections).map(([key, section]) => (
                  <div key={key}>
                    <button
                      onClick={() => setSelectedSection(key)}
                      className={`flex items-center space-x-2 w-full text-left px-3 py-2 rounded-lg transition-colors duration-200 ${
                        selectedSection === key
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {section.icon}
                      <span className="font-medium">{section.title}</span>
                    </button>
                    {selectedSection === key && (
                      <ul className="mt-2 space-y-1">
                        {section.items.map(item => (
                          <li key={item.id}>
                            <button
                              onClick={() => setSelectedItem(item.id)}
                              className={`w-full text-left pl-11 pr-3 py-2 text-sm rounded-lg transition-colors duration-200 ${
                                selectedItem === item.id
                                  ? 'bg-primary-50 text-primary-700'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {item.title}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-9">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                {getCurrentContent()}

                {/* Version Selector */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">Documentation version</div>
                    <select className="text-sm border-gray-300 rounded-md">
                      <option>v2.0 (Latest)</option>
                      <option>v1.9</option>
                      <option>v1.8</option>
                    </select>
                  </div>
                </div>

                {/* Feedback Section */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900">Was this page helpful?</h4>
                  <div className="mt-4 flex space-x-4">
                    <button className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                      Yes
                    </button>
                    <button className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                      No
                    </button>
                  </div>
                </div>

                {/* Interactive Action Buttons */}
                <div className="mt-12 border-t border-gray-200 pt-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Ready to Get Started?</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => handleActionButton('browse-apps')}
                      className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                    >
                      Browse Applications
                    </button>
                    <button
                      onClick={() => handleActionButton('deploy')}
                      className="flex items-center justify-center px-4 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
                    >
                      Deploy an App
                    </button>
                    <button
                      onClick={() => handleActionButton('insights')}
                      className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                    >
                      View Insights
                    </button>
                  </div>
                </div>

                {/* Related Resources */}
                <div className="mt-12 border-t border-gray-200 pt-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Related Resources</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <a href="#" className="group block p-4 border border-gray-200 rounded-lg hover:border-primary-500">
                      <h4 className="text-sm font-medium text-gray-900 group-hover:text-primary-600">API Reference</h4>
                      <p className="mt-1 text-sm text-gray-500">Complete API documentation for developers</p>
                    </a>
                    <a href="#" className="group block p-4 border border-gray-200 rounded-lg hover:border-primary-500">
                      <h4 className="text-sm font-medium text-gray-900 group-hover:text-primary-600">Video Tutorials</h4>
                      <p className="mt-1 text-sm text-gray-500">Step-by-step video guides</p>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 